import type { ButtonHTMLAttributes } from 'react';
import { CaretRight, CheckCircle, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface OptionCardProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  label: string;
  subtitle?: string;
  icon?: PhosphorIcon;
  selected?: boolean;
}

/** Figma "Option Card" (21:81): full-width choice row with optional icon, subtitle and trailing chevron. */
export function OptionCard({
  label,
  subtitle,
  icon,
  selected = false,
  type = 'button',
  className,
  ...props
}: OptionCardProps) {
  return (
    <button
      type={type}
      aria-pressed={selected}
      className={cx(
        'flex min-h-(--mbm-size-list-row-default) w-full items-center gap-(--mbm-space-3) rounded-(--mbm-component-radius-input) border-[1.5px] bg-surface-muted px-(--mbm-space-5) py-(--mbm-space-3) text-left',
        'transition-opacity duration-(--mbm-duration-fast) hover:opacity-(--mbm-opacity-pressed)',
        'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        'disabled:cursor-not-allowed disabled:opacity-(--mbm-opacity-disabled)',
        selected ? 'border-action-primary' : 'border-transparent',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span className={selected ? 'text-action-primary' : 'text-text-primary'}>
          <Icon icon={icon} size="xl" />
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-label-large text-text-primary">{label}</span>
        {subtitle ? <span className="text-body-small text-text-secondary">{subtitle}</span> : null}
      </span>
      {/* Figma: the chosen row swaps its chevron for a check. */}
      <span className={selected ? 'text-action-primary' : 'text-text-muted'}>
        <Icon icon={selected ? CheckCircle : CaretRight} size="lg" />
      </span>
    </button>
  );
}
